import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { OrderDTO } from '@/models/OrderDTO';
import { ordersApi } from '../../services/rest/apiInstances';
import { buildClientApiConfig } from '@/helpers/apiRequestConfig';

export const fetchOrdersList = createAsyncThunk('orders/fetchOrdersList', async (filters: any) => {
    try {
        const options = buildClientApiConfig();
        const data = await ordersApi.getOrders(filters, options);
        return data.data;
    } catch (error: any) {
        return error.data;
    }
});

export const fetchOrdersListWithSearch = createAsyncThunk(
    'orders/fetchOrdersListWithSearch',
    async (searchBy: string) => {
        try {
            const data = await ordersApi.searchOrder({
                searchOrderRequest: { reference: searchBy },
            });
            return data.data;
        } catch (error: any) {
            return error.data;
        }
    },
);

export interface IOrdersState {
    isPending: boolean;
    errors: any;
    orderList: OrderDTO[];
    isShowSearchResults: boolean;
    orderListSearchResult: any[];
    isOpenPaymentInstructionModal: boolean;
    isOpenCreatePaymentInstructionModal: boolean;
    paymentInstructionToDisplay: any;
    createPaymentInstructionToDisplay: any;
    isOpenInboundPaymentDetailsBladeModal: boolean;
    inboundPaymentDetailsToDisplay: any;
    isSimulateTransactionModalOpen: boolean;
    isOpenActivateNowPaymentModal: boolean;
}

const initialState: IOrdersState = {
    isPending: false,
    errors: null,
    orderList: [],
    isShowSearchResults: false,
    orderListSearchResult: [],
    isOpenPaymentInstructionModal: false,
    paymentInstructionToDisplay: undefined,
    createPaymentInstructionToDisplay: undefined,
    isOpenCreatePaymentInstructionModal: false,
    isOpenInboundPaymentDetailsBladeModal: false,
    inboundPaymentDetailsToDisplay: undefined,
    isSimulateTransactionModalOpen: false,
    isOpenActivateNowPaymentModal: false,
};

export const ordersSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        openInstructionModal: (state, action: PayloadAction<any>) => {
            state.isOpenPaymentInstructionModal = true;
            state.paymentInstructionToDisplay = action.payload;
        },
        closeInstructionModal: (state) => {
            state.isOpenPaymentInstructionModal = false;
            state.paymentInstructionToDisplay = undefined;
        },
        openCreateInstructionModal: (state, action: PayloadAction<any>) => {
            state.isOpenCreatePaymentInstructionModal = true;
            state.createPaymentInstructionToDisplay = action.payload;
        },
        closeCreateInstructionModal: (state) => {
            state.isOpenCreatePaymentInstructionModal = false;
            state.createPaymentInstructionToDisplay = undefined;
        },

        openInboundPaymentDetailModal: (state, action: PayloadAction<any>) => {
            state.isOpenInboundPaymentDetailsBladeModal = true;
            state.inboundPaymentDetailsToDisplay = action.payload;
        },
        closeInboundPaymentDetailModal: (state) => {
            state.isOpenInboundPaymentDetailsBladeModal = false;
            state.inboundPaymentDetailsToDisplay = undefined;
        },
        openSimulateTransactionModal: (state, action: PayloadAction<any>) => {
            state.isSimulateTransactionModalOpen = true;
            state.paymentInstructionToDisplay = action.payload;
        },
        closeSimulateTransactionModal: (state) => {
            state.isSimulateTransactionModalOpen = false;
            state.paymentInstructionToDisplay = undefined;
        },

        openActivateNowPaymentModal: (state, action: PayloadAction<any>) => {
            state.isOpenActivateNowPaymentModal = true;
            state.paymentInstructionToDisplay = action.payload;
        },
        closeActivateNowPaymentModal: (state) => {
            state.isOpenActivateNowPaymentModal = false;
            state.paymentInstructionToDisplay = undefined;
        },
        setISShowSearchResults: (state, action: PayloadAction<boolean>) => {
            state.isShowSearchResults = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrdersList.pending, (state) => {
                state.isPending = true;
                state.isShowSearchResults = false;
            })
            .addCase(fetchOrdersList.rejected, (state, action) => {
                state.isPending = false;
                state.errors = action.error;
            })
            .addCase(fetchOrdersList.fulfilled, (state, action) => {
                const orders = action.payload?.orders;

                state.isPending = false;
                state.orderList = orders;
            })

            .addCase(fetchOrdersListWithSearch.pending, (state) => {
                state.isPending = true;
                state.isShowSearchResults = true;
                state.orderListSearchResult = [];
            })
            .addCase(fetchOrdersListWithSearch.rejected, (state, action) => {
                state.isPending = false;
                state.errors = action.error;
            })
            .addCase(fetchOrdersListWithSearch.fulfilled, (state, action) => {
                const orders = action.payload;
                state.isPending = false;
                state.orderListSearchResult = orders;
            });
    },
});

export const {
    openInstructionModal,
    closeInstructionModal,
    closeCreateInstructionModal,
    openCreateInstructionModal,
    openInboundPaymentDetailModal,
    closeInboundPaymentDetailModal,
    openSimulateTransactionModal,
    closeSimulateTransactionModal,
    openActivateNowPaymentModal,
    closeActivateNowPaymentModal,
    setISShowSearchResults,
} = ordersSlice.actions;

export default ordersSlice.reducer;
